import { useCallback, useRef } from 'react';

export interface SaveRequest {
  /**
   * The bundles this save writes. Empty for state no bundle owns, such as the
   * deck's scalar fields or the bundle order.
   */
  bundleIds: string[];
  run: () => Promise<void>;
}

export interface SaveQueueCallbacks {
  /** A save failed twice. The queue remains paused until the user retries it. */
  onSaveFailed: (args: {
    bundleIds: string[];
    error: unknown;
    retry: () => void;
  }) => void;
}

/** One retry. A second failure of the same payload is a refusal, not a hiccup. */
const MAX_ATTEMPTS = 2;

async function attempt(request: SaveRequest): Promise<void> {
  for (let tries = 1; tries <= MAX_ATTEMPTS; tries += 1) {
    try {
      // Awaited inside the try, so a synchronous throw fails the same way a
      // rejection does.
      await request.run();
      return;
    } catch (error) {
      if (tries === MAX_ATTEMPTS) throw error;
    }
  }
}

/**
 * Runs presentation saves one at a time, in request order. Every save queues and
 * nothing is collapsed because each payload is a change since the previous one.
 *
 * A failed save is retried once, then pauses the queue until the user explicitly
 * retries it. The failed save and all later work remain in order because each
 * payload was diffed from the state produced by the payload before it.
 */
export function usePresentationSaveQueue(callbacks: SaveQueueCallbacks): {
  requestSave: (request: SaveRequest) => void;
} {
  const isDrainingRef = useRef(false);
  const pendingRef = useRef<SaveRequest[]>([]);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const requestSave = useCallback((request: SaveRequest) => {
    pendingRef.current.push(request);
    if (isDrainingRef.current) return;

    const drain = (): void => {
      const next = pendingRef.current.shift();
      if (!next) {
        isDrainingRef.current = false;
        return;
      }
      isDrainingRef.current = true;

      const run = (): void => {
        void attempt(next).then(drain, (error: unknown) => {
          let retryStarted = false;
          callbacksRef.current.onSaveFailed({
            bundleIds: next.bundleIds,
            error,
            retry: () => {
              if (retryStarted) return;
              retryStarted = true;
              run();
            },
          });
        });
      };

      run();
    };

    drain();
  }, []);

  return { requestSave };
}
